import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { FormikErrors, useFormik } from 'formik'
import { Dialog } from 'primereact/dialog'
import { Stepper, StepperRefAttributes } from 'primereact/stepper'
import { StepperPanel } from 'primereact/stepperpanel'

import { DeliveryForecastDespatchAdvicePackagingMaterial } from '../../../../../../../data/models'
import {
  HandleableError,
  getHandleableError,
} from '../../../../../../../utils/errors/isXiaError'
import { getCultureFromLanguage } from '../../../../../../../utils/helper/getCultureFromLanguage'
import { isEmpty } from '../../../../../../../utils/helper/isEmpty'
import { SupportedLanguage } from '../../../../../../../utils/i18n'
import {
  requestPrepareUploadablePackagingMaterial,
  requestUploadPackagingMaterial,
} from '../../../data'
import { VerifyPanel } from './StepperPanels'
import { SummaryPanel } from './StepperPanels'
import { UploadPanel } from './StepperPanels'
import { UploadPanelFormValues } from './common/UploadPanelFormValues'

type MasterDataDialogProps = {
  visible: boolean
  onHide: () => void
}

export const MasterDataUploadDialog: React.FC<MasterDataDialogProps> = ({
  onHide,
  visible,
}) => {
  const { t, i18n } = useTranslation()
  const stepperRef = useRef<StepperRefAttributes | null>(null)
  const [deleteExistingPackages, setDeleteExistingPackages] =
    useState<boolean>(false)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [error, setError] = useState<HandleableError | null>(null)
  const [data, setData] = useState<
    DeliveryForecastDespatchAdvicePackagingMaterial[] | null
  >(null)

  const uploadPanelFormik = useFormik<UploadPanelFormValues>({
    initialValues: {
      csv: undefined,
      deleteExistingPackages: true,
      reusablePackagingFrom: getDefaultValueForReusable(
        i18n.language as SupportedLanguage,
      ),
      disposablePackagingFrom: getDefaultValueForDisposable(
        i18n.language as SupportedLanguage,
      ),
    },
    validate: (data: UploadPanelFormValues) => {
      let errors: FormikErrors<UploadPanelFormValues> = {}

      if (isEmpty(data.csv))
        errors.csv = t(
          'masterDataManagement.packagingData.table.upload.dialog.errors.required',
          {
            fieldName: t(
              'masterDataManagement.packagingData.table.upload.dialog.uploadPanel.fields.csv',
            ),
          },
        ) //
      if (!data.reusablePackagingFrom)
        errors.reusablePackagingFrom = t(
          'masterDataManagement.packagingData.table.upload.dialog.errors.required',
          {
            fieldName: t(
              'masterDataManagement.packagingData.table.upload.dialog.uploadPanel.fields.reusablePackagingFrom.label',
            ),
          },
        ) //
      if (!data.disposablePackagingFrom)
        errors.disposablePackagingFrom = t(
          'masterDataManagement.packagingData.table.upload.dialog.errors.required',
          {
            fieldName: t(
              'masterDataManagement.packagingData.table.upload.dialog.uploadPanel.fields.disposablePackagingFrom.label',
            ),
          },
        ) //

      return errors
    },
    onSubmit: async (data: UploadPanelFormValues) => {
      onUploadCsv(data)
    },
  })

  useEffect(() => {
    uploadPanelFormik.initialValues.disposablePackagingFrom =
      getDefaultValueForDisposable(i18n.language as SupportedLanguage)
    uploadPanelFormik.initialValues.reusablePackagingFrom =
      getDefaultValueForReusable(i18n.language as SupportedLanguage)
  }, [i18n.language, uploadPanelFormik.initialValues])

  const uploadCsv = async (
    csv: File,
    disposablePackagingFrom: string = 'disposablePackaging',
    reusablePackagingFrom: string = 'reusablePackaging',
  ) => {
    try {
      setIsLoading(true)
      const culture = getCultureFromLanguage(i18n.language)
      const result = await requestPrepareUploadablePackagingMaterial(
        csv,
        culture,
        reusablePackagingFrom,
        disposablePackagingFrom,
      )
      setData(result)
    } catch (error) {
      const handleableError = getHandleableError(error)
      setError(handleableError)
    } finally {
      setIsLoading(false)
    }
  }

  const uploadData = async () => {
    try {
      setIsLoading(true)
      await requestUploadPackagingMaterial(data!, deleteExistingPackages)
    } catch (error) {
      const handleableError = getHandleableError(error)
      setError(handleableError)
    } finally {
      setIsLoading(false)
    }
  }

  const onUploadCsv = (data: UploadPanelFormValues) => {
    uploadCsv(
      data.csv!,
      data.disposablePackagingFrom,
      data.reusablePackagingFrom,
    )
    stepperRef.current?.nextCallback()
  }

  const onVerifyNext = () => {
    uploadData()
    stepperRef.current?.nextCallback()
  }

  const onVerifyPrev = () => {
    setError(null)
    setData(null)
    uploadPanelFormik.resetForm()
    stepperRef.current?.prevCallback()
  }

  const onExit = () => {
    uploadPanelFormik.resetForm()
    setIsLoading(false)
    setError(null)
    setData(null)
    setDeleteExistingPackages(false)
    onHide()
  }

  const header = (
    <>{t('masterDataManagement.packagingData.table.upload.dialog.title')}</>
  )
  const footer = <></>

  return (
    <Dialog
      onHide={onExit}
      visible={visible}
      header={header}
      footer={footer}
      style={{ width: '75rem' }}
    >
      <Stepper ref={stepperRef} linear>
        <StepperPanel
          header={t(
            'masterDataManagement.packagingData.table.upload.dialog.uploadPanel.title',
          )}
        >
          <UploadPanel formik={uploadPanelFormik} />
        </StepperPanel>
        <StepperPanel
          header={t(
            'masterDataManagement.packagingData.table.upload.dialog.verifyPanel.title',
          )}
        >
          <VerifyPanel
            data={data || undefined}
            error={error || undefined}
            deleteExistingPackages={deleteExistingPackages}
            setDeleteExistingPackages={setDeleteExistingPackages}
            onNext={onVerifyNext}
            onPrev={onVerifyPrev}
            isLoading={isLoading}
          />
        </StepperPanel>
        <StepperPanel
          header={t(
            'masterDataManagement.packagingData.table.upload.dialog.summaryPanel.title',
          )}
        >
          <SummaryPanel
            onExit={onExit}
            isLoading={isLoading}
            error={error || undefined}
            data={data || undefined}
          />
        </StepperPanel>
      </Stepper>
    </Dialog>
  )
}

function getDefaultValueForReusable(lang?: SupportedLanguage) {
  if (!lang) return ''

  switch (lang) {
    case 'de':
      return 'Mehrweg'
    case 'en':
      return 'reusable'
    default:
      throw new Error(`Language "${lang}" is not supported`)
  }
}

function getDefaultValueForDisposable(lang?: SupportedLanguage) {
  if (!lang) return ''

  switch (lang) {
    case 'de':
      return 'Einweg'
    case 'en':
      return 'disposable'
    default:
      throw new Error(`Language "${lang}" is not supported`)
  }
}
